import { useContext, useEffect, useState, useCallback, useRef } from "react";
import imageCompression from "browser-image-compression";
import { Input } from "../components/Input";
import { PhotoUploadInput } from "../components/PhotoUploadInput";
import { PrimaryButtonSmall, SecondaryButton } from "../components/Button";
import ThemeToggle from "../components/ThemeToggle";
import PlatformFeatures from "./PlatformFeatures";
import { GlobalContext } from "../context/GlobalContext";
import VendorAPI from "../api/VendorApi";
import UserAPI from "../api/UserApi";
import EventAPI from "../api/EventApi";
import { callFunction } from "../api/callFunction";
import { showToast } from "../utils/showToast";
import Utility from "../utils/utility";
import { capture, EVENTS } from "../analytics";

// Image compression configuration
const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1024,
  useWebWorker: true,
};

// Format integer cents as a USD string, e.g. 7000 -> "$70.00"
const formatUSD = Utility.formatUSD;

// Host-facing phrase for a payout section's event window ("your first 2 events"
// vs "your next 3 events" / "…from Jul 1"). Reads the new `window` shape and
// falls back to the legacy `firstNEvents` scalar so older configs still read.
const hostWindowPhrase = (section) => {
  const w = section && section.window;
  if (w && w.type === "next") {
    const base = `your next ${w.count} event${w.count === 1 ? "" : "s"}`;
    if (w.anchorMode === "date") {
      const fmt = { month: "short", day: "numeric", year: "numeric" };
      const anchor = Utility.convertDate(w.anchorDate ?? w.anchorDateMs);
      const day = anchor ? anchor.toLocaleDateString("en-US", fmt) : "";
      return day ? `${base} from ${day}` : base;
    }
    return base;
  }
  const n = w && w.type === "first" && w.count != null ? w.count : section && section.firstNEvents;
  return `your first ${n} event${n === 1 ? "" : "s"}`;
};

// Per-event payout status pill styling.
const STATUS_STYLES = {
  available: { label: "Available", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  pending: { label: "Pending", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
  processing: { label: "Processing", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  paid: { label: "Paid", cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
};

// Helper function to compress and upload a photo
const uploadCompressedPhoto = async (file, uploadFunction, id) => {
  const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);
  return await uploadFunction(id, compressedFile);
};

// Helper function to update vendor profile
const updateVendorProfile = async (vendorID, meetupMemberID, bio) => {
  return await VendorAPI.updateVendorProfile(vendorID, meetupMemberID, bio);
};

// Helper function to update user profile and related events
const updateUserProfile = async (userID, name, photoURL) => {
  const updatedUser = await UserAPI.updateUserProfile(userID, name, photoURL);

  // Update user data in all active events
  try {
    await EventAPI.updateUserInActiveEvents(userID, name, photoURL);
  } catch (error) {
    console.error("Error updating user in active events:", error);
    // Don't block the user profile update if event updates fail
  }

  return updatedUser;
};

/**
 * Settings component for managing user and vendor profiles.
 * Handles profile updates, photo uploads, and Stripe account setup.
 */
export default function Settings() {
  const { globalState, setGlobalState } = useContext(GlobalContext);

  const [meetupMemberID, setMeetupMemberID] = useState(globalState?.vendor?.meetupMemberID || "");
  const [vendorBio, setVendorBio] = useState(globalState?.vendor?.bio || "");
  const [fullName, setFullName] = useState(globalState?.user?.name || "");
  const [userPhoto, setUserPhoto] = useState({ file: null, preview: globalState?.user?.photoURL || "" });
  const [isUploading, setIsUploading] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(true);
  const [payoutLoading, setPayoutLoading] = useState(false);

  // Detect if any field has been changed
  const hasChanges = useCallback(() => {
    const meetupMemberIDChanged = meetupMemberID !== (globalState?.vendor?.meetupMemberID || "");
    const vendorBioChanged = vendorBio !== (globalState?.vendor?.bio || "");
    const fullNameChanged = fullName !== (globalState?.user?.name || "");
    const userPhotoChanged = userPhoto.file !== null;

    return meetupMemberIDChanged || vendorBioChanged || fullNameChanged || userPhotoChanged;
  }, [meetupMemberID, vendorBio, fullName, userPhoto.file, globalState?.vendor?.meetupMemberID, globalState?.vendor?.bio, globalState?.user?.name]);

  const handleUserPhotoChange = (file, preview) => {
    setUserPhoto({ file, preview });
  };

  // Handle form submission to update profiles
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);

    try {
      // Determine what has changed
      const meetupMemberIDChanged = meetupMemberID !== (globalState?.vendor?.meetupMemberID || "");
      const vendorBioChanged = vendorBio !== (globalState?.vendor?.bio || "");
      const fullNameChanged = fullName !== (globalState?.user?.name || "");
      const userPhotoChanged = userPhoto.file !== null;

      // Update vendor profile if meetup ID or bio changed
      if (meetupMemberIDChanged || vendorBioChanged) {
        const bioToSave = vendorBioChanged ? vendorBio.trim() : undefined;
        const vendor = await updateVendorProfile(globalState.vendorID, meetupMemberID, bioToSave);

        // Update global state with the returned vendor data, ensuring meetupMemberID is included
        setGlobalState((prevState) => ({
          ...prevState,
          vendor: {
            ...prevState.vendor,
            ...vendor,
            meetupMemberID, // Ensure the new meetupMemberID is set in global state
          },
        }));
      }

      // Update user profile if name or photo changed
      if ((fullNameChanged || userPhotoChanged) && globalState?.user?.userID) {
        // Upload new photo if provided, otherwise use existing
        const userPhotoURL = userPhotoChanged
          ? await uploadCompressedPhoto(userPhoto.file, UserAPI.uploadUserPhoto, globalState.user.userID)
          : globalState?.user?.photoURL || "";

        const updatedUser = await updateUserProfile(globalState.user.userID, fullName, userPhotoURL);

        // Preserve any pre-existing user fields not returned by the API
        setGlobalState((prevState) => ({
          ...prevState,
          user: { ...prevState.user, ...updatedUser },
        }));

        setUserPhoto(prev => ({ ...prev, file: null }));
      }

      // WHICH fields changed, never their values — `fullName`, `vendorBio` and
      // `meetupMemberID` are all on the sanitizer's blocklist, and a "saved"
      // event that carried them would be the exact leak this surface's
      // instrumentation was held back over for so long.
      capture(EVENTS.HOST_PROFILE_SAVED, {
        context: "settings",
        changedMeetupID: meetupMemberIDChanged,
        changedBio: vendorBioChanged,
        changedName: fullNameChanged,
        changedPhoto: userPhotoChanged,
      });
      showToast("Settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      showToast("Failed to save settings", "error");
    } finally {
      setIsUploading(false);
    }
  };

  // Open Stripe in a new tab. The backend returns the right link for the
  // host's state: hosted onboarding before setup is complete, a single-use
  // Express Dashboard login link (for bank/payout updates) once it is.
  const fetchStripeAccountLink = useCallback(async () => {
    // Open a blank window immediately to avoid pop-up blockers
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      // Show loading message while fetching the link
      newWindow.document.body.innerHTML = `
        <div style="font-family: Arial, sans-serif; text-align: center; padding-top: 50px;">
          <h2 style="color: #333;">Connecting to Stripe...</h2>
          <p style="color: #666;">Please wait while we securely redirect you to Stripe.</p>
          <p style="color: #999; font-size: 0.9em;">This may take a few moments.</p>
        </div>
      `;
      newWindow.document.title = "Loading...";
    }

    try {
      // Call Firebase function to get the Stripe link
      const result = await callFunction('stripeConnectAccountLink');
      const link = result.accountLinkUrl;

      if (newWindow) {
        // Redirect the window to the Stripe link
        newWindow.location.href = link;
      } else {
        // Fallback for pop-up blockers
        showToast("Please allow pop-ups for this site to connect your Stripe account.", "error");
        console.error("Pop-up blocked. Please allow pop-ups for this site.");
      }
    } catch (error) {
      // Don't leave the placeholder "Connecting to Stripe..." tab orphaned
      if (newWindow) {
        newWindow.close();
      }
      console.error("Error fetching Stripe link:", error);
      showToast("Failed to open Stripe. Please try again.", "error");
    }
  }, []);

  // Guards for loadEarnings: drop out-of-order responses (a focus-triggered
  // refetch must not overwrite fresher post-payout data) and remember whether
  // we ever loaded, so refreshes revalidate quietly instead of blanking the UI.
  const earningsReqRef = useRef(0);
  const hasEarningsRef = useRef(false);

  // Load the host's earnings ledger (available / pending / paid).
  const loadEarnings = useCallback(async () => {
    const reqId = ++earningsReqRef.current;
    if (!hasEarningsRef.current) {
      setEarningsLoading(true);
    }
    try {
      const result = await callFunction("getHostEarnings");
      if (reqId !== earningsReqRef.current) return; // superseded by a newer request
      hasEarningsRef.current = true;
      setEarnings(result);
    } catch (error) {
      console.error("Error loading earnings:", error);
      // Keep last-known-good data on refresh failures; only blank on first load.
      if (reqId === earningsReqRef.current && !hasEarningsRef.current) {
        setEarnings(null);
      }
    } finally {
      if (reqId === earningsReqRef.current) {
        setEarningsLoading(false);
      }
    }
  }, []);

  // Request a payout of the matured balance to the host's Stripe account.
  const handleRequestPayout = useCallback(async () => {
    setPayoutLoading(true);
    try {
      const result = await callFunction("requestHostPayout");
      showToast(`Payout of ${formatUSD(result.amountCents)} sent to your Stripe account`);
      await loadEarnings();
    } catch (error) {
      console.error("Error requesting payout:", error);
      showToast(error.message || "Failed to request payout", "error");
    } finally {
      setPayoutLoading(false);
    }
  }, [loadEarnings]);

  useEffect(() => {
    loadEarnings();
  }, [loadEarnings]);

  // Refresh when the tab regains focus — e.g. the host just finished Stripe
  // onboarding in the popup and came back, so the button/nag should update.
  useEffect(() => {
    const onFocus = () => loadEarnings();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadEarnings]);

  // Fresh server truth once earnings load; until then (or if the call fails)
  // fall back to the cached vendor doc so the button doesn't mislabel.
  const stripeOnboarded = earnings
    ? !!earnings.onboardingComplete
    : !!globalState?.vendor?.stripeOnboardingComplete;

  useEffect(() => {
    setMeetupMemberID(globalState?.vendor?.meetupMemberID || "");
    setVendorBio(globalState?.vendor?.bio || "");
    setFullName(globalState?.user?.name || "");
    setUserPhoto(prev => ({ ...prev, preview: globalState?.user?.photoURL || "" }));
  }, [globalState.vendor, globalState.vendorID, globalState.user]);


  return (
    <form onSubmit={handleSubmit}>
      <div className="max-w-4xl mx-auto p-4 bg-white dark:bg-gray-800 rounded-md shadow-md mt-6">
        {/* User Profile Section */}
        {globalState?.user && (
          <div className="border-b border-gray-900/10 dark:border-white/10 pb-6">
            <div className="flex items-center justify-between border-b border-gray-900/10 dark:border-white/10 pb-2">
              <h2 className="  text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">
                User Profile
              </h2>
              <div className="flex items-center gap-x-6">


                <button
                  type="submit"
                  disabled={isUploading || !hasChanges()}
                  className={`rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${hasChanges()
                    ? "bg-primary text-white hover:bg-primaryDark focus-visible:outline-primary"
                    : "bg-gray-400 text-white cursor-not-allowed"
                    }`}
                >
                  {isUploading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
              {/* User Photo Upload */}
              <PhotoUploadInput
                label="Profile Photo"
                preview={userPhoto.preview}
                onChange={handleUserPhotoChange}
              />

              {/* User Name */}
              <div className="sm:col-span-4">
                <Input
                  label="Full name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Appearance Section */}
        <div className="border-b border-gray-900/10 dark:border-white/10 pb-6 mt-4">
          <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">
            Appearance
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Choose a light or dark theme, or follow your system setting.
          </p>
          <div className="mt-4">
            <ThemeToggle />
          </div>
        </div>

        {/* Platform switches (clientConfig/features). Super admins only: the
            callables behind it refuse everyone else. */}
        {globalState?.isSuperAdmin && <PlatformFeatures />}

        {/* Vendor Profile Section */}
        <div className="border-b border-gray-900/10 dark:border-white/10 pb-6 mt-4">
          <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">
            Vendor Profile
          </h2>

          <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
            {/* Meetup Member ID */}
            <div className="sm:col-span-4">
              <Input
                label="Meetup Member ID"
                type="text"
                value={meetupMemberID}
                onChange={(e) => setMeetupMemberID(e.target.value)}
              />
            </div>

            {/* Host Bio — shown to attendees on event pages */}
            <div className="sm:col-span-6">
              <label
                htmlFor="vendor-bio"
                className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
              >
                Bio
              </label>
              <div className="mt-1">
                <textarea
                  id="vendor-bio"
                  name="vendor-bio"
                  rows={4}
                  value={vendorBio}
                  onChange={(e) => setVendorBio(e.target.value)}
                  placeholder="A short intro shown to attendees on your event pages. Why you host, what your nights feel like, what people can expect."
                  className="px-4 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-base sm:leading-6 resize-y"
                />
              </div>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                Shown publicly on your event pages alongside your name and photo.
              </p>
            </div>
          </div>
        </div>

        {/* Stripe Section */}
        <div className=" pb-6 mt-4">
          <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">
            Payment Settings
          </h2>

          {/* One state-aware button: onboarding link before setup is complete,
              Express Dashboard login link after (hosts can't use the regular
              dashboard.stripe.com login). */}
          <div className="mt-6">
            <SecondaryButton
              onClick={fetchStripeAccountLink}
            >
              {stripeOnboarded ? "Manage Stripe Account" : "Set up Stripe"}
            </SecondaryButton>
            {stripeOnboarded && (
              <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                Opens your Stripe dashboard to update bank details, payout settings, and account info.
              </p>
            )}
          </div>

          {/* Earnings & Payouts */}
          <div className="mt-6 rounded-md border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Earnings</h3>
            {/* The split is superAdmin-configurable per host, so the client has
                no correct default: a missing hostRate used to render a
                confident 70% that could differ from what the host is actually
                paid. Show the section only when the server told us the rate
                (GIS-194). */}
            {earnings?.effective?.hostRate != null && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                You receive {Math.round(earnings.effective.hostRate * 1000) / 10}% of each event's ticket revenue
                {earnings.effective.introRate?.enabled
                  ? ` (${Math.round(earnings.effective.introRate.rate * 1000) / 10}% for ${hostWindowPhrase(earnings.effective.introRate)})`
                  : ""}
                {earnings.effective.minPayout?.enabled
                  ? `, with a $${(earnings.effective.minPayout.amountCents || 0) / 100} minimum on ${hostWindowPhrase(earnings.effective.minPayout)}.`
                  : "."}
              </p>
            )}

            {earningsLoading ? (
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">Loading earnings…</p>
            ) : earnings ? (
              <>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Available</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatUSD(earnings.available?.cents)}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {earnings.available?.eventCount || 0} event{earnings.available?.eventCount === 1 ? "" : "s"} ready
                    </p>
                  </div>
                  <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Pending</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatUSD(earnings.pending?.cents)}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Matures 3 days after each event</p>
                  </div>
                  <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Paid out</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatUSD(earnings.paid?.cents)}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Lifetime</p>
                  </div>
                </div>

                {!earnings.onboardingComplete && (
                  <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                    Finish Stripe onboarding above to withdraw your earnings.
                  </p>
                )}

                {earnings.effective?.autoPayout?.enabled && earnings.onboardingComplete && (
                  <p className="mt-3 text-xs text-green-700 dark:text-green-400">
                    Automatic payouts are on. Available funds are sent to your bank every day, so
                    you don't need to request anything.
                  </p>
                )}

                <div className="mt-4">
                  {earnings.onboardingComplete && (earnings.available?.cents || 0) > 0 ? (
                    <PrimaryButtonSmall
                      onClick={handleRequestPayout}
                      loading={payoutLoading}
                      loadingLabel="Sending…"
                    >
                      Request Payout — {formatUSD(earnings.available.cents)}
                    </PrimaryButtonSmall>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="text-white bg-gray-400 py-2 px-4 rounded cursor-not-allowed inline-flex items-center"
                    >
                      Request Payout
                    </button>
                  )}
                </div>

                {/* Per-event breakdown */}
                {earnings.breakdown && earnings.breakdown.length > 0 && (
                  <div className="mt-5 border-t border-gray-100 dark:border-gray-800 pt-4">
                    <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">Per-event breakdown</p>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-400 dark:text-gray-500">
                            <th className="py-1 pr-3 font-medium">Event</th>
                            <th className="py-1 pr-3 font-medium">Date</th>
                            <th className="py-1 pr-3 font-medium text-right">Gross</th>
                            <th className="py-1 pr-3 font-medium text-right">Your share</th>
                            <th className="py-1 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {earnings.breakdown.map((ev) => (
                            <tr key={ev.id} className="border-t border-gray-100 dark:border-gray-800">
                              <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">{ev.name}</td>
                              <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">
                                {ev.dateMs ? new Date(ev.dateMs).toLocaleDateString() : "—"}
                              </td>
                              <td className="py-2 pr-3 text-right text-gray-500 dark:text-gray-400">{formatUSD(ev.revenueCents)}</td>
                              <td className="py-2 pr-3 text-right font-medium text-gray-900 dark:text-gray-100">
                                {formatUSD(ev.hostCents)}
                                {ev.introApplied && (
                                  <span
                                    className="ml-1 inline-flex rounded-full bg-sky-100 dark:bg-sky-900/40 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300"
                                    title={ev.appliedHostRate != null ? `Boosted rate ${Math.round(ev.appliedHostRate * 1000) / 10}% applied` : "Boosted intro rate applied"}
                                  >
                                    {ev.appliedHostRate != null ? `${Math.round(ev.appliedHostRate * 1000) / 10}%` : "boost"}
                                  </span>
                                )}
                                {ev.guaranteeApplied && (
                                  <span
                                    className="ml-1 inline-flex rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300"
                                    title="Minimum payout guarantee applied"
                                  >
                                    min
                                  </span>
                                )}
                              </td>
                              <td className="py-2"><StatusBadge status={ev.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">Couldn’t load earnings right now.</p>
            )}
          </div>
        </div>

        {/* Bottom Save Button */}
        <div className="flex  pt-6 border-t border-gray-900/10 dark:border-white/10">
          <button
            type="submit"
            disabled={isUploading || !hasChanges()}
            className={`rounded-md px-8 py-2 text-md w-full sm:w-60 font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${hasChanges()
              ? "bg-primary text-white hover:bg-primaryDark focus-visible:outline-primary"
              : "bg-gray-400 text-white cursor-not-allowed"
              }`}
          >
            {isUploading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </form>
  );
}


